import React, { useEffect, useRef, useState } from 'react';
import { MapPinIcon } from '@heroicons/react/24/outline';

// Google Maps API Key from environment variable
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

/**
 * Load Google Maps JavaScript API dynamically
 */
let googleMapsLoaded = false;
let googleMapsLoading = false;
const loadGoogleMapsScript = () => {
  return new Promise((resolve, reject) => {
    // Already loaded
    if (googleMapsLoaded) {
      resolve();
      return;
    }

    // Already loading
    if (googleMapsLoading) {
      // Wait for the script to load
      const checkLoaded = setInterval(() => {
        if (googleMapsLoaded) {
          clearInterval(checkLoaded);
          resolve();
        }
      }, 100);
      return;
    }

    // No API key configured
    if (!GOOGLE_MAPS_API_KEY || GOOGLE_MAPS_API_KEY === 'your_google_maps_api_key_here') {
      console.warn('Google Maps API key not configured. Please set REACT_APP_GOOGLE_MAPS_API_KEY in your .env file');
      reject(new Error('Google Maps API key not configured'));
      return;
    }

    googleMapsLoading = true;

    // Create script element
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      googleMapsLoaded = true;
      googleMapsLoading = false;
      resolve();
    };

    script.onerror = () => {
      googleMapsLoading = false;
      reject(new Error('Failed to load Google Maps script'));
    };

    document.head.appendChild(script);
  });
};

/**
 * AddressAutocomplete Component
 * Uses Google Places Autocomplete to search and fill address fields
 *
 * @param {Object} props
 * @param {Function} props.onPlaceSelected - Callback when a place is selected
 * @param {string} props.initialValue - Initial value for the input
 * @param {string} props.className - Additional CSS classes
 */
const AddressAutocomplete = ({ onPlaceSelected, initialValue = '', className = '' }) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [inputValue, setInputValue] = useState(initialValue);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Load Google Maps script
    loadGoogleMapsScript()
      .then(() => {
        setIsLoading(false);
        setError(null);

        // Initialize autocomplete
        if (inputRef.current && window.google && window.google.maps && window.google.maps.places) {
          const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
            types: ['address'],
            fields: ['address_components', 'formatted_address', 'geometry', 'name']
          });

          autocompleteRef.current = autocomplete;

          // Listen for place selection
          autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();

            if (!place.address_components) {
              return;
            }

            // Parse address components
            const addressData = parseAddressComponents(place.address_components);
            addressData.formattedAddress = place.formatted_address || '';

            // Call the callback
            onPlaceSelected(addressData);
            setInputValue(place.formatted_address || '');
          });
        }
      })
      .catch((err) => {
        setIsLoading(false);
        setError(err.message);
        console.error('Error loading Google Maps:', err);
      });

    return () => {
      if (autocompleteRef.current && window.google) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [onPlaceSelected]);

  // Update input value when initialValue changes
  useEffect(() => {
    setInputValue(initialValue);
  }, [initialValue]);

  return (
    <div className="relative">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <MapPinIcon className="h-5 w-5 text-gray-400" />
      </div>
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        placeholder={
          isLoading
            ? "Loading Google Maps..."
            : error
            ? "Address search not available"
            : "Search for an address..."
        }
        disabled={isLoading || error}
        className={`block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed ${className}`}
      />
      {error && (
        <p className="mt-1 text-xs text-red-600">
          {error}. Please check your .env configuration.
        </p>
      )}
    </div>
  );
};

/**
 * Parse Google Places address components into a structured object
 */
function parseAddressComponents(components) {
  const addressData = {
    streetAddress: '',
    city: '',
    state: '',
    postalCode: '',
    country: ''
  };

  // Street number
  const streetNumber = components.find(c => c.types.includes('street_number'))?.long_name || '';

  // Route (street name)
  const route = components.find(c => c.types.includes('route'))?.long_name || '';

  // Combine street number and route
  addressData.streetAddress = `${streetNumber} ${route}`.trim();

  // City (locality or postal_town or administrative_area_level_2)
  const locality = components.find(c => c.types.includes('locality'));
  const postalTown = components.find(c => c.types.includes('postal_town'));
  const adminArea2 = components.find(c => c.types.includes('administrative_area_level_2'));
  addressData.city = locality?.long_name || postalTown?.long_name || adminArea2?.long_name || '';

  // State/Province (administrative_area_level_1)
  const adminArea1 = components.find(c => c.types.includes('administrative_area_level_1'));
  addressData.state = adminArea1?.long_name || '';

  // Postal Code
  const postalCode = components.find(c => c.types.includes('postal_code'));
  addressData.postalCode = postalCode?.long_name || '';

  // Country
  const country = components.find(c => c.types.includes('country'));
  addressData.country = country?.long_name || '';

  return addressData;
}

export default AddressAutocomplete;
